import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { sanityClient } from "../lib/sanityClient";
import MechanicCard from "./MechanicCard";

const MechanicsList = () => {
  const { t } = useTranslation();
  const { lang } = useParams();

  const [mechanics, setMechanics] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOption, setSortOption] = useState("experience");

  const mechanicsPerPage = 6;

  useEffect(() => {
    const fetchMechanics = async () => {
      try {
        const query = `*[_type == "mechanic"] | order(yearsOfExperience desc) {
          _id,
          name,
          phoneNumber,
          location,
          specialties,
          yearsOfExperience,
          rating
        }`;
        const data = await sanityClient.fetch(query);
        setMechanics(data);
      } catch (error) {
        console.error("Error fetching mechanics:", error);
      }
    };

    fetchMechanics();
  }, []);

  const getLocalizedText = (field) => {
    return field?.[lang] || field?.en || "";
  };

  const filteredMechanics = mechanics.filter((mechanic) => {
    const nameMatch = mechanic.name.toLowerCase().includes(searchQuery.toLowerCase());
    const specialtyMatch = mechanic.specialties.some((spec) =>
      getLocalizedText(spec).toLowerCase().includes(searchQuery.toLowerCase())
    );
    const locationMatch = getLocalizedText(mechanic.location)
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    return nameMatch || specialtyMatch || locationMatch;
  });

  const sortedMechanics = [...filteredMechanics].sort((a, b) => {
    switch (sortOption) {
      case "experience":
        return b.yearsOfExperience - a.yearsOfExperience;
      case "rating":
        return b.rating - a.rating;
      case "specialty":
        return getLocalizedText(a.specialties[0])?.localeCompare(getLocalizedText(b.specialties[0]));
      default:
        return 0;
    }
  });

  const indexOfLastMechanic = currentPage * mechanicsPerPage;
  const indexOfFirstMechanic = indexOfLastMechanic - mechanicsPerPage;
  const currentMechanics = sortedMechanics.slice(indexOfFirstMechanic, indexOfLastMechanic);

  const nextPage = () => {
    if (currentPage < Math.ceil(filteredMechanics.length / mechanicsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className="container mx-auto p-4">
      {/* Search Bar */}
      <div className="mb-4 flex justify-center">
        <input
          type="text"
          placeholder={t("mechanics.searchPlaceholder")}
          className="p-2 w-full max-w-lg border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Sorting Controls */}
      <div className="flex justify-between mb-4">
        <label className="text-lg font-semibold">
          {t("mechanics.sortBy")}{" "}
          <select
            className="p-2 border rounded-md"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
          >
            <option value="experience">{t("mechanics.sort.experience")}</option>
            <option value="rating">{t("mechanics.sort.rating")}</option>
            <option value="specialty">{t("mechanics.sort.specialty")}</option>
          </select>
        </label>
      </div>

      {/* Mechanics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {currentMechanics.length > 0 ? (
          currentMechanics.map((mechanic) => (
            <MechanicCard key={mechanic._id} mechanic={mechanic} lang={lang} />
          ))
        ) : (
          <p className="text-gray-500 text-center col-span-3">
            {t("mechanics.notFound")}
          </p>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredMechanics.length > mechanicsPerPage && (
        <div className="flex justify-between mt-6">
          <button
            onClick={prevPage}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-300 text-black rounded-md disabled:opacity-50"
          >
            {t("pagination.previous")}
          </button>
          <span className="text-lg font-semibold">
            {t("pagination.page")} {currentPage}
          </span>
          <button
            onClick={nextPage}
            disabled={currentPage >= Math.ceil(filteredMechanics.length / mechanicsPerPage)}
            className="px-4 py-2 bg-gray-300 text-black rounded-md disabled:opacity-50"
          >
            {t("pagination.next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default MechanicsList;
